import { NavLink, Outlet, Link } from "react-router-dom";
import { Bell, Moon, Search, Sun, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../store/auth";
import { useTheme } from "../store/theme";
import { api } from "../lib/api";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "./Logo";

type Suggest = { markets: { slug: string; question: string }[]; categories: { slug: string; name: string }[] };

export function Shell() {
  const { user, refresh, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [q, setQ] = useState("");
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const suggest = useQuery({
    queryKey: ["suggest", q],
    queryFn: () => api<Suggest>(`/markets/search/suggest?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });

  return (
    <>
      <div className="chrome">
        <header className="topnav">
          <div className="topnav-inner">
            <Link to="/" className="sw-logo-link" aria-label="Softwave Markets home">
              <Logo markSize={36} />
            </Link>
            <nav aria-label="Primary">
              <NavLink to="/markets">Markets</NavLink>
              <NavLink to="/markets?sort=trending">Trending</NavLink>
              <NavLink to="/portfolio">Portfolio</NavLink>
              <NavLink to="/learn">Learn</NavLink>
            </nav>
            <div className="search">
              <Search size={16} />
              <input
                placeholder="Search markets"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search markets"
              />
              {q.length >= 2 && suggest.data && (
                <div className="search-pop" role="listbox">
                  {suggest.data.markets.map((m) => (
                    <Link key={m.slug} to={`/markets/${m.slug}`} onClick={() => setQ("")} className="search-hit">
                      {m.question}
                    </Link>
                  ))}
                  {!suggest.data.markets.length && <div className="search-empty">No markets match that search.</div>}
                </div>
              )}
            </div>
            <div className="topnav-right">
              <button
                className="btn btn-ghost theme-toggle"
                onClick={() => toggle()}
                aria-label={theme === "night" ? "Switch to day mode" : "Switch to night mode"}
                title={theme === "night" ? "Day mode" : "Night mode"}
                type="button"
              >
                {theme === "night" ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              {user ? (
                <>
                  <Link to="/activity" className="icon-btn" aria-label="Notifications"><Bell size={18} /></Link>
                  <Link to="/wallet" className="btn btn-ghost btn-sm"><Wallet size={16} /> Wallet</Link>
                  <Link to="/account" className="nav-user">{user.username}</Link>
                  {(user.role === "ADMIN" || user.role === "SUPER_ADMIN") && <Link to="/admin">Admin</Link>}
                  <button className="btn btn-ghost btn-sm" onClick={() => void logout()}>Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-ghost btn-sm">Sign in</Link>
                  <Link to="/register" className="btn btn-primary btn-sm">My account</Link>
                </>
              )}
            </div>
          </div>
        </header>
      </div>
      <Outlet />
      <Footer />
      <nav className="mobile-nav" aria-label="Mobile">
        <NavLink to="/">Home</NavLink>
        <NavLink to="/markets">Markets</NavLink>
        <NavLink to="/portfolio">Portfolio</NavLink>
        <NavLink to="/activity">Activity</NavLink>
        <NavLink to="/account">Account</NavLink>
      </nav>
    </>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>
          <Logo markSize={40} />
          <p className="footer-blurb">A prediction-market exchange. Users trade with each other. Softwave matches, accounts and settles — and earns a transparent fee.</p>
        </div>
        <div>
          <h4>Product</h4>
          <Link to="/markets">Markets</Link>
          <Link to="/how-it-works">How it works</Link>
          <Link to="/learn">Learn</Link>
          <Link to="/fees">Fee schedule</Link>
        </div>
        <div>
          <h4>Trust</h4>
          <Link to="/trust">Trust center</Link>
          <Link to="/status">System status</Link>
          <Link to="/resolution-rules">Resolution rules</Link>
          <Link to="/responsible-trading">Responsible trading</Link>
        </div>
        <div>
          <h4>Legal</h4>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/risk">Risk disclosure</Link>
          <Link to="/disputes">Disputes</Link>
        </div>
      </div>
      <div className="legal">Softwave Markets does not take the opposite side of ordinary customer flow. Demo data is labeled. This software is not an offer of services in any jurisdiction.</div>
    </footer>
  );
}
